import { useState } from "react";
import { Text, Tooltip, ActionIcon, Group, ThemeIcon } from "@mantine/core";
import { Badge, DataTable, Card, StatusBadge } from "@xplatform/design-system";
import type { DataTableColumn } from "@xplatform/design-system";
import {
    IconInfoCircle,
    IconDownload,
    IconFile,
    IconFileTypePdf,
    IconPhoto,
    IconFileSpreadsheet,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ProcessStatus } from "@models/extraction";
import type { IProcessItemDetailViewModel } from "@models/extraction";
import { intelligenceEngineApi } from "@services/extractionApi";
import { formatDate } from "@utils/formatters";
import "./ProcessItemsTable.scss";

interface ProcessItemsTableProps {
    items: IProcessItemDetailViewModel[];
    processId: string;
    downloadAllButton?: React.ReactNode;
}

const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    switch (ext) {
        case "pdf":
            return <IconFileTypePdf size={16} />;
        case "png":
        case "jpg":
        case "jpeg":
        case "gif":
        case "webp":
        case "tiff":
        case "tif":
            return <IconPhoto size={16} />;
        case "xlsx":
        case "xls":
        case "csv":
            return <IconFileSpreadsheet size={16} />;
        default:
            return <IconFile size={16} />;
    }
};

export const ProcessItemsTable = ({ items, processId, downloadAllButton }: ProcessItemsTableProps) => {
    const { t } = useTranslation();
    const [downloadingItemId, setDownloadingItemId] = useState<string | null>(null);

    const handleDownloadInput = async (e: React.MouseEvent, itemId: string) => {
        e.stopPropagation();
        setDownloadingItemId(itemId);
        try {
            const result = await intelligenceEngineApi.downloadItemInputFile(processId, itemId);
            window.open(result.s3_key, "_blank");
        } catch (err) {
            console.error("Error downloading input file:", err);
        } finally {
            setDownloadingItemId(null);
        }
    };

    const completedCount = items.filter((i) => i.status === ProcessStatus.Completed).length;
    const failedCount = items.filter((i) => i.status === ProcessStatus.Failed).length;

    const columns: DataTableColumn<IProcessItemDetailViewModel>[] = [
        {
            key: "itemId",
            header: t("admin.monitoring.processItems.itemId"),
            width: "12%",
            render: (item) => (
                <Tooltip label={item.id} position="top">
                    <Text size="sm" className="process-items-table__item-id">
                        {item.idShort}
                    </Text>
                </Tooltip>
            ),
        },
        {
            key: "fileName",
            header: t("admin.monitoring.processItems.fileName"),
            width: "28%",
            render: (item) => (
                <Group gap="xs" wrap="nowrap">
                    <ThemeIcon size="sm" variant="light" color="gray" radius="sm">
                        {getFileIcon(item.fileName)}
                    </ThemeIcon>
                    <Text size="sm" fw={500} truncate="end">
                        {item.fileName}
                    </Text>
                </Group>
            ),
        },
        {
            key: "status",
            header: t("admin.monitoring.processItems.status"),
            width: "12%",
            render: (item) => (
                <StatusBadge size="sm" variant="light" status={item.status}>
                    {item.status}
                </StatusBadge>
            ),
        },
        {
            key: "created",
            header: t("admin.monitoring.processItems.created"),
            width: "20%",
            render: (item) => (
                <Text size="sm" c="dimmed">
                    {formatDate(item.createdAt)}
                </Text>
            ),
        },
        {
            key: "error",
            header: t("admin.monitoring.processItems.error"),
            render: (item) =>
                item.errorMessage ? (
                    <Group gap={4} wrap="nowrap">
                        <Text size="sm" className="process-items-table__error" truncate="end">
                            {item.errorMessage}
                        </Text>
                        <Tooltip label={item.errorMessage} position="top" multiline maw={400}>
                            <ActionIcon size="xs" variant="subtle" color="red">
                                <IconInfoCircle size={14} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                ) : (
                    <Text size="sm" c="dimmed">
                        -
                    </Text>
                ),
        },
        {
            key: "download",
            header: "",
            width: "5%",
            align: "center",
            render: (item) => (
                <Tooltip label={t("admin.monitoring.download.downloadInput")} position="top">
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="blue"
                        loading={downloadingItemId === item.id}
                        onClick={(e) => handleDownloadInput(e, item.id)}
                        aria-label={t("admin.monitoring.download.downloadInput")}
                    >
                        <IconDownload size={16} />
                    </ActionIcon>
                </Tooltip>
            ),
        },
    ];

    return (
        <Card withBorder className="process-items-table">
            <div className="process-items-table__header">
                <Group gap="sm" align="center">
                    <Text size="md" fw={600}>
                        {t("admin.monitoring.processItems.title")}
                    </Text>
                    <Badge size="sm" variant="light" color="blue">
                        {t("admin.monitoring.processItems.item", { count: items.length })}
                    </Badge>
                    {completedCount > 0 && (
                        <Badge size="sm" variant="light" color="green">
                            {t("admin.monitoring.processItems.completed", { count: completedCount })}
                        </Badge>
                    )}
                    {failedCount > 0 && (
                        <Badge size="sm" variant="light" color="red">
                            {t("admin.monitoring.processItems.failed", { count: failedCount })}
                        </Badge>
                    )}
                </Group>
                {downloadAllButton}
            </div>

            <DataTable<IProcessItemDetailViewModel>
                className="process-items-table__table"
                striped
                highlightOnHover
                withTableBorder
                fz="sm"
                verticalSpacing="sm"
                horizontalSpacing="md"
                columns={columns}
                data={items}
                getRowKey={(item) => item.id}
                emptyMessage={t("admin.monitoring.processItems.noItems")}
            />
        </Card>
    );
};
